import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { RequireAuth } from '@/components/RequireAuth';
import { AppShell } from '@/layouts/AppShell';
import { Login } from '@/pages/Login';
import { Register } from '@/pages/Register';
import { Dashboard } from '@/pages/Dashboard';
import { ResidentDashboard } from '@/pages/ResidentDashboard';
import { CondominiAccessi } from '@/pages/CondominiAccessi';
import { RegistroAccessi } from '@/pages/RegistroAccessi';
import { BachecaCondomini } from '@/pages/BachecaCondomini';
import { RicevuteNumerazione } from '@/pages/RicevuteNumerazione';
import { DichiarazioniPagamento } from '@/pages/DichiarazioniPagamento';
import { InformativaCondomini } from '@/pages/InformativaCondomini';
import { Cruscotto } from '@/pages/Cruscotto';
import { Assemblea } from '@/pages/Assemblea';
import { Registry } from '@/pages/Registry';
import { Documents } from '@/pages/Documents';
import { Suppliers } from '@/pages/Suppliers';
import { Expenses } from '@/pages/Expenses';
import { Communications } from '@/pages/Communications';
import { Meetings } from '@/pages/Meetings';
import { Accounting } from '@/pages/Accounting';
import PrimaNota from '@/pages/PrimaNota';
import { Reports } from '@/pages/Reports';
import { Doorman } from '@/pages/Doorman';
import { Tax } from '@/pages/Tax';
import { Settings } from '@/pages/Settings';
import { Millesimi } from '@/pages/Millesimi';
import OpeningBalances from '@/pages/OpeningBalances';
import { Allocations } from '@/pages/Allocations';
import { MonthlyQuotes } from '@/pages/MonthlyQuotes';
import { Incidents } from '@/pages/Incidents';
import { Portineria } from '@/pages/Portineria';
import { Budgets } from '@/pages/Budgets';
import { BankImport } from '@/pages/BankImport';
import { Reconciliation } from '@/pages/Reconciliation';
import { Exports } from '@/pages/Exports';
import { Backups } from '@/pages/Backups';
import { Workspace } from '@/pages/Workspace';
import { Invites } from '@/pages/Invites';
import { Onboarding } from '@/pages/Onboarding';
import { InviteAccept } from '@/pages/InviteAccept';
import { SuperAdmin } from '@/pages/SuperAdmin';
import { ResetPassword } from '@/pages/ResetPassword';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ChangePassword } from '@/pages/ChangePassword';
import { WorkspaceStatus } from '@/pages/WorkspaceStatus';
import Pricing from '@/pages/Pricing';
import Billing from '@/pages/Billing';
import NotificationSettings from '@/pages/NotificationSettings';
import NotificationOutbox from '@/pages/NotificationOutbox';
import NotificationPreferences from '@/pages/NotificationPreferences';
import AdminHealth from '@/pages/AdminHealth';
import AdminPayments from '@/pages/AdminPayments';
import CommunicationsCenter from '@/pages/CommunicationsCenter';
import MyCommunications from '@/pages/MyCommunications';
import BrandingSettings from '@/pages/BrandingSettings';
import AuditLog from '@/pages/AuditLog';
import TreatmentRegistry from '@/pages/TreatmentRegistry';
import RetentionSettings from '@/pages/RetentionSettings';
import SchedulerAdmin from '@/pages/SchedulerAdmin';
import DocumentShareLinks from '@/pages/DocumentShareLinks';
import StaffManagement from '@/pages/StaffManagement';
import StaffDetail from '@/pages/StaffDetail';
import MyStaffPortal from '@/pages/MyStaffPortal';
import StaffConsultant from '@/pages/StaffConsultant';
import PaymentSettings from '@/pages/PaymentSettings';
import PaymentResult from '@/pages/PaymentResult';
import FattureInCloudStatus from '@/pages/FattureInCloudStatus';
import FAQ from '@/pages/FAQ';
import { Guida } from '@/pages/Guida';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import CookiePolicy from '@/pages/CookiePolicy';
import ContractTerms from '@/pages/ContractTerms';

/*
  Il menu porta a /communications-center senza l'id del condominio, ma la pagina esiste solo nella
  forma /communications-center/:condominiumId: il risultato era una pagina bianca («No routes matched»).
  Qui si rimanda al condominio selezionato, conservando gli eventuali parametri (per esempio ?apri=).
  Se nessun condominio e' selezionato si torna alla dashboard, dove si sceglie.
*/
export const CentroComunicazioniSenzaId = () => {
  const { selectedCondominium } = useAuth();
  const { search } = useLocation();
  const id = selectedCondominium?.condominium?.id || selectedCondominium?.id;
  return <Navigate to={id ? `/communications-center/${id}${search || ''}` : '/dashboard'} replace />;
};

// Il condomino vede il suo cruscotto personale, non quello dell'amministrazione
const DashboardPerRuolo = () => {
  const { selectedCondominium } = useAuth();
  return selectedCondominium?.role === 'RESIDENT' ? <ResidentDashboard /> : <Dashboard />;
};

// Pagine non ancora aperte ai condomini (bilanci, cruscotto con le criticita'): se ci arrivano scrivendo l'indirizzo, tornano alla dashboard
const SoloAmministrazione = ({ children }) => {
  const { selectedCondominium } = useAuth();
  return selectedCondominium?.role === 'RESIDENT' ? <Navigate to="/dashboard" replace /> : children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
            <Route path="/workspace/status" element={<RequireAuth><WorkspaceStatus /></RequireAuth>} />
            <Route path="/" element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPerRuolo />} />
              <Route path="accessi-condomini" element={<CondominiAccessi />} />
              <Route path="registro-accessi" element={<RegistroAccessi />} />
              <Route path="bacheca-condomini" element={<BachecaCondomini />} />
              <Route path="informativa-condomini" element={<InformativaCondomini />} />
              <Route path="dichiarazioni-pagamento" element={<SoloAmministrazione><DichiarazioniPagamento /></SoloAmministrazione>} />
              <Route path="ricevute-numerazione" element={<SoloAmministrazione><RicevuteNumerazione /></SoloAmministrazione>} />
              <Route path="cruscotto" element={<SoloAmministrazione><Cruscotto /></SoloAmministrazione>} />
              <Route path="assemblea" element={<Assemblea />} />
              <Route path="change-password" element={<ChangePassword />} />
              <Route path="guida" element={<Guida />} />
              <Route path="workspace" element={<Workspace />} />
              <Route path="workspace/billing" element={<Billing />} />
              <Route path="workspace/terms" element={<ContractTerms />} />
              <Route path="invites" element={<Invites />} />
              <Route path="superadmin" element={<SuperAdmin />} />
              <Route path="superadmin/fatture" element={<FattureInCloudStatus />} />
              <Route path="admin/health" element={<AdminHealth />} />
              <Route path="admin/payments" element={<AdminPayments />} />
              <Route path="registry" element={<Registry />} />
              <Route path="documents" element={<Documents />} />
              <Route path="documents/:documentId/share" element={<DocumentShareLinks />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="communications" element={<Communications />} />
              <Route path="communications-center" element={<CentroComunicazioniSenzaId />} />
              <Route path="communications-center/:condominiumId" element={<CommunicationsCenter />} />
              <Route path="my-communications" element={<MyCommunications />} />
              <Route path="meetings" element={<Meetings />} />
              <Route path="accounting" element={<Accounting />} />
              <Route path="prima-nota" element={<PrimaNota />} />
              <Route path="millesimi" element={<Millesimi />} />
              <Route path="opening-balances" element={<OpeningBalances />} />
              <Route path="allocations" element={<Allocations />} />
              <Route path="monthly-quotes" element={<MonthlyQuotes />} />
              <Route path="portineria" element={<Portineria />} />
              <Route path="incidents" element={<Incidents />} />
              <Route path="budgets" element={<SoloAmministrazione><Budgets /></SoloAmministrazione>} />
              <Route path="bank-import" element={<BankImport />} />
              <Route path="reconciliation" element={<Reconciliation />} />
              <Route path="exports" element={<Exports />} />
              <Route path="backups" element={<Backups />} />
              <Route path="reports" element={<Reports />} />
              <Route path="doorman" element={<Doorman />} />
              <Route path="staff" element={<StaffManagement />} />
              <Route path="staff/my" element={<MyStaffPortal />} />
              <Route path="staff/consultant" element={<StaffConsultant />} />
              <Route path="staff/:staffMemberId" element={<StaffDetail />} />
              <Route path="tax" element={<Tax />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/branding" element={<BrandingSettings />} />
              <Route path="settings/notifications" element={<NotificationSettings />} />
              <Route path="settings/retention" element={<RetentionSettings />} />
              <Route path="settings/payments" element={<PaymentSettings />} />
              <Route path="payment-result" element={<PaymentResult />} />
              <Route path="privacy/registro-trattamenti" element={<TreatmentRegistry />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="admin/scheduler" element={<SchedulerAdmin />} />
              <Route path="notifications/outbox" element={<NotificationOutbox />} />
              <Route path="profile/notifications" element={<NotificationPreferences />} />
            </Route>
          </Routes>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;